import api from "../api.js";

const SESSION_KEY = "homevilas.ai.session.v2";
const ANON_KEY = "homevilas.ai.anon";
const LOCAL_SESSION_PREFIX = "local-homevilas-saathi";
const LOCAL_STATE_KEY = "homevilas.saathi.local.state";
const KNOWN_DESTINATIONS = [
  "goa",
  "north goa",
  "south goa",
  "kerala",
  "karala",
  "rajasthan",
  "udaipur",
  "jaipur",
  "shimla",
  "manali",
  "kasol",
  "lonavala",
  "alibaug",
  "mumbai",
  "maharashtra",
  "navi mumbai",
  "powai",
  "delhi",
  "delhi ncr",
  "gurgaon",
  "bengaluru",
  "bangalore",
  "munnar",
  "himachal",
  "uttarakhand",
];
const DESTINATION_ALIASES = {
  karala: "Kerala",
  bangalore: "Bengaluru",
  gurgaon: "Delhi NCR",
};
const AMENITY_HINTS = {
  pool: "Private Pool",
  swimming: "Private Pool",
  jacuzzi: "Jacuzzi",
  pet: "Pet Friendly",
  pets: "Pet Friendly",
  garden: "Garden",
  projector: "Projector",
  party: "Party Hall",
  beach: "Beachside",
  parking: "Parking",
};
const HINDI_PATTERN = /[\u0900-\u097f]|\b(kya|hai|chahiye|dikhao|batao|kitna|kiraya|rate|price|mehnga|sasta|guest|log|ke liye|mein|me|wale|wali|karna|book)\b/i;
const GREETING_PATTERN = /^(hi|hii|hello|hey|namaste|namaskar|good morning|good afternoon|good evening|start|yo)$/i;
const AFFIRMATIVE_PATTERN = /\b(yes|yeah|yep|ok|okay|confirm|continue|proceed|book|booking|haan|ha|han|theek|thik|chalo|kar do|kardo)\b/i;
const DETAIL_PATTERN = /\b(detail|details|description|about|photos|more|batao|dikhao|jankari|jaankari)\b/i;
const MONTHS = {
  jan: 0,
  january: 0,
  feb: 1,
  february: 1,
  mar: 2,
  march: 2,
  apr: 3,
  april: 3,
  may: 4,
  jun: 5,
  june: 5,
  jul: 6,
  july: 6,
  aug: 7,
  august: 7,
  sep: 8,
  sept: 8,
  spt: 8,
  september: 8,
  oct: 9,
  october: 9,
  nov: 10,
  november: 10,
  dec: 11,
  december: 11,
};

const randomId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `anon-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export const getAnonymousSessionId = () => {
  try {
    const existing = localStorage.getItem(ANON_KEY);
    if (existing) return existing;
    const created = randomId();
    localStorage.setItem(ANON_KEY, created);
    return created;
  } catch (_error) {
    return randomId();
  }
};

export const getStoredAiSessionUuid = () => {
  try {
    return localStorage.getItem(SESSION_KEY) || "";
  } catch (_error) {
    return "";
  }
};

export const storeAiSessionUuid = (sessionUuid) => {
  try {
    if (sessionUuid) localStorage.setItem(SESSION_KEY, sessionUuid);
  } catch (_error) {
    // Ignore storage failures; the chat can still work for the active page session.
  }
};

export const clearStoredAiSession = () => {
  try {
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(LOCAL_STATE_KEY);
  } catch (_error) {
    // Ignore storage failures; the chat can start a fresh in-memory session.
  }
};

const isRecoverableAiFailure = (error) => {
  const status = Number(error?.response?.status || 0);
  return !status || status === 404 || status === 503 || status >= 500;
};

const createLocalSession = (initial = {}) => {
  const sessionUuid = `${LOCAL_SESSION_PREFIX}-${getAnonymousSessionId()}`;
  const session = {
    id: sessionUuid,
    session_uuid: sessionUuid,
    sessionUuid,
    status: "local",
    current_stage: "START",
    currentStage: "START",
    initial,
  };
  storeAiSessionUuid(sessionUuid);
  return session;
};

const isLocalSession = (sessionUuid = "") => String(sessionUuid).startsWith(LOCAL_SESSION_PREFIX);

const readLocalState = () => {
  try {
    return JSON.parse(localStorage.getItem(LOCAL_STATE_KEY) || "{}") || {};
  } catch (_error) {
    return {};
  }
};

const writeLocalState = (state) => {
  try {
    localStorage.setItem(LOCAL_STATE_KEY, JSON.stringify(state || {}));
  } catch (_error) {
    // Keep the current chat usable even if storage is unavailable.
  }
};

const formatIsoDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const prettyDate = (iso = "") => {
  if (!iso) return "";
  const [year, month, day] = iso.split("-");
  return `${day}-${month}-${year}`;
};

const normalizeDestination = (value = "") =>
  DESTINATION_ALIASES[String(value || "").trim().toLowerCase()] ||
  String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, (match) => match.toUpperCase());

const isHindiLike = (content = "") => HINDI_PATTERN.test(String(content || ""));

const parseDateFromText = (content = "") => {
  const text = String(content || "").toLowerCase();
  const numeric = text.match(/\b(\d{1,2})[/-](\d{1,2})(?:[/-](\d{2,4}))?\b/);
  let day = null;
  let month = null;
  let year = null;
  if (numeric) {
    day = Number(numeric[1]);
    month = Number(numeric[2]) - 1;
    year = numeric[3] ? Number(numeric[3]) : new Date().getFullYear();
  } else {
    const named = text.match(/\b(\d{1,2})[\s/-]*([a-z]{3,9})(?:[\s,/-]*(\d{2,4}))?\b/);
    if (named && Object.prototype.hasOwnProperty.call(MONTHS, named[2])) {
      day = Number(named[1]);
      month = MONTHS[named[2]];
      year = named[3] ? Number(named[3]) : new Date().getFullYear();
    }
  }
  if (year !== null && year < 100) year += 2000;
  if (!Number.isInteger(day) || !Number.isInteger(month) || !Number.isInteger(year)) return "";
  const parsed = new Date(year, month, day);
  if (parsed.getFullYear() !== year || parsed.getMonth() !== month || parsed.getDate() !== day) return "";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (parsed < today && !/\d{4}/.test(text)) parsed.setFullYear(parsed.getFullYear() + 1);
  return formatIsoDate(parsed);
};

const parseDatesFromText = (content = "") => {
  const text = String(content || "").toLowerCase();
  const values = [];
  const pushDate = (raw) => {
    const parsed = parseDateFromText(raw);
    if (parsed && !values.includes(parsed)) values.push(parsed);
  };
  for (const match of text.matchAll(/\b\d{1,2}[/-]\d{1,2}(?:[/-]\d{2,4})?\b/g)) pushDate(match[0]);
  for (const match of text.matchAll(/\b\d{1,2}[\s/-]*[a-z]{3,9}(?:[\s,/-]*\d{2,4})?\b/g)) pushDate(match[0]);
  return values;
};

const parseGuestsFromText = (content = "") => {
  const text = String(content || "").toLowerCase();
  const explicit = text.match(/\b(\d{1,2})\s*(guest|guests|adult|adults|person|persons|log|people)\b/);
  if (explicit) return Math.max(1, Number(explicit[1]) || 0);
  const onlyNumber = text.trim().match(/^(\d{1,2})$/);
  if (onlyNumber) return Math.max(1, Number(onlyNumber[1]) || 0);
  return 0;
};

const parseBedroomsFromText = (content = "") => {
  const text = String(content || "").toLowerCase();
  const match = text.match(/\b(\d{1,2})\s*(bhk|bed|beds|bedroom|bedrooms|room|rooms)\b/);
  return match ? Math.max(1, Number(match[1]) || 0) : 0;
};

const parseBudgetFromText = (content = "") => {
  const text = String(content || "").toLowerCase().replace(/,/g, "");
  const under = /\b(under|below|less than|budget|andar|tak|upto|up to)\b/.test(text);
  const match = text.match(/(?:rs\.?|inr|₹)?\s*(\d{4,7})(?:\s*(?:rs|inr|₹))?/i);
  if (!match) return {};
  const amount = Number(match[1]);
  if (!Number.isFinite(amount) || amount <= 0) return {};
  return under ? { maxPrice: amount } : { budget: amount };
};

const parseAmenitiesFromText = (content = "") => {
  const text = String(content || "").toLowerCase();
  return Object.entries(AMENITY_HINTS)
    .filter(([word]) => new RegExp(`\\b${word}\\b`).test(text))
    .map(([, label]) => label)
    .filter((label, index, values) => values.indexOf(label) === index);
};

const parseDestinationFromText = (content = "") => {
  const raw = String(content || "").trim();
  const text = raw.toLowerCase();
  if (!raw || GREETING_PATTERN.test(raw)) return "";
  const found = KNOWN_DESTINATIONS.find((destination) => new RegExp(`\\b${destination.replace(/\s+/g, "\\s+")}\\b`).test(text));
  if (found) return normalizeDestination(found);
  if (parseDateFromText(raw) || parseGuestsFromText(raw)) return "";
  if (!/[a-z]/i.test(raw) || raw.length > 60) return "";
  const cleaned = raw
    .replace(/\b(stay|stays|villa|villas|property|properties|chahiye|dikhao|search|book|booking|mein|me|for)\b/gi, " ")
    .replace(/[^a-z\s]/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!cleaned || cleaned.length < 3 || GREETING_PATTERN.test(cleaned)) return "";
  if (!/\s/.test(cleaned) && !KNOWN_DESTINATIONS.includes(cleaned.toLowerCase())) return "";
  return cleaned ? normalizeDestination(cleaned) : "";
};

const asRows = (payload) =>
  Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload?.items)
    ? payload.items
    : Array.isArray(payload?.results)
    ? payload.results
    : Array.isArray(payload)
    ? payload
    : [];

const mediaValue = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value.image_url || value.url || value.src || value.path || "";
};

const toRecommendation = (row = {}) => {
  const providerId = row.provider_property_id || row.providerPropertyId || (String(row.id || "").startsWith("provider:") ? String(row.id).split(":").pop() : "");
  const detailPath =
    row.detail_path ||
    row.link ||
    (providerId ? `/stays/homevilas/${encodeURIComponent(providerId)}` : row.slug ? `/property/${encodeURIComponent(row.slug)}` : "/listings");
  const image =
    mediaValue(row.thumbnail || row.thumbnail_url || row.featured_image || row.cover_image || row.image || row.image_url) ||
    mediaValue(Array.isArray(row.images) ? row.images[0] : "");
  const price =
    row.display_price ||
    row.display_sell_price ||
    row.final_per_night_display_price ||
    row.sell_price_per_night ||
    row.price_per_night ||
    row.base_price_per_night ||
    row.price;
  return {
    id: String(row.id || providerId || row.slug || row.title),
    name: row.title || row.name || row.property_name || "Homevilas stay",
    location: [row.area, row.city, row.state].filter(Boolean).join(", ") || row.location || "",
    image,
    bedrooms: row.bedrooms || row.beds || "",
    maxGuests: row.max_guests || row.guests || "",
    rating: row.avg_rating || row.rating || "",
    currency: row.currency || "INR",
    detailPath,
    description: row.short_description || row.shortDescription || row.description || row.long_description || "",
    pricing: {
      totalStayAmount: price,
      label: row.display_price_label || row.price_subtitle || "PER NIGHT",
    },
  };
};

const selectedSummary = (property = {}) => {
  const parts = [
    property.location ? `Location: ${property.location}` : "",
    property.bedrooms ? `${property.bedrooms} bedrooms` : "",
    property.maxGuests ? `${property.maxGuests} guests allowed` : "",
    property.pricing?.totalStayAmount ? `Price from ${property.pricing.totalStayAmount}` : "",
  ].filter(Boolean);
  const description = String(property.description || "").replace(/\s+/g, " ").trim();
  return [parts.join(" | "), description ? description.slice(0, 260) : ""].filter(Boolean).join("\n");
};

const searchLocalProperties = async (state = {}) => {
  if (!state.destination) return { rows: [], total: 0, url: "/listings" };
  const params = new URLSearchParams({
    destination: state.destination,
    limit: "3",
    page: "1",
  });
  if (state.guests) {
    params.set("adults", String(state.guests));
    params.set("guests", String(state.guests));
  }
  if (state.checkIn && state.checkOut) {
    params.set("checkIn", state.checkIn);
    params.set("checkOut", state.checkOut);
  }
  if (state.bedrooms) params.set("beds", String(state.bedrooms));
  if (state.maxPrice) params.set("maxPrice", String(state.maxPrice));
  const { data } = await api.get(`/public/properties?${params.toString()}`, { timeout: 12000 });
  const rows = asRows(data).map(toRecommendation);
  const total = Number(data?.pagination?.total ?? data?.meta?.total ?? rows.length) || rows.length;
  const listingParams = new URLSearchParams({ city: state.destination });
  if (state.checkIn) listingParams.set("checkIn", state.checkIn);
  if (state.checkOut) listingParams.set("checkOut", state.checkOut);
  if (state.guests) {
    listingParams.set("adults", String(state.guests));
    listingParams.set("children", "0");
  }
  if (state.bedrooms) listingParams.set("beds", String(state.bedrooms));
  if (state.maxPrice) listingParams.set("maxPrice", String(state.maxPrice));
  return { rows, total, url: `/listings?${listingParams.toString()}` };
};

const localChatTurn = async (sessionUuid, content = "", options = {}) => {
  const text = String(content || "").trim();
  const lower = text.toLowerCase();
  const hindi = isHindiLike(text);
  const state = { ...readLocalState() };
  const selectedProperty = options?.structuredData?.property;

  if (selectedProperty?.id) {
    state.selectedProperty = selectedProperty;
    writeLocalState(state);
    return {
      reply: hindi
        ? `${selectedProperty.name} select ho gayi.\n${selectedSummary(selectedProperty)}\n\nKya aap is property ko book karna chahte hain? Haan likhenge to booking page open karne ka option de dunga.`
        : `${selectedProperty.name} is selected.\n${selectedSummary(selectedProperty)}\n\nWould you like to book this property? Reply yes and I will give you the booking link.`,
      session: { sessionUuid, currentStage: "PROPERTY_SELECTED" },
    };
  }

  if (GREETING_PATTERN.test(text)) {
    return {
      reply: hindi
        ? "Namaste. Aap destination ya requirement likhiye, jaise 'Goa pool villa 6 guests 21-09-2026 to 23-09-2026'. Main matching properties dhoondh kar cards dikhaunga."
        : "Hi. Tell me what you need, for example 'Goa pool villa for 6 guests from 21-09-2026 to 23-09-2026'. I will search and show matching property cards.",
      session: { sessionUuid, currentStage: "START" },
    };
  }

  if (state.selectedProperty && AFFIRMATIVE_PATTERN.test(text)) {
    const property = state.selectedProperty;
    const bookingPath = property.detailPath || "/listings";
    return {
      reply: hindi
        ? `Booking continue karne ke liye ye property page open karein: ${bookingPath}. Wahan dates/guests confirm karke final payment tak ja sakte hain.`
        : `Continue booking here: ${bookingPath}. Confirm dates and guests there, then proceed to final payment.`,
      session: { sessionUuid, currentStage: "PAYMENT_REQUIRED", payment: { checkoutPath: bookingPath } },
    };
  }

  if (state.selectedProperty && DETAIL_PATTERN.test(text)) {
    const property = state.selectedProperty;
    return {
      reply: hindi
        ? `${property.name}\n${selectedSummary(property)}\n\nBook karna hai to 'haan' ya 'book' likhiye.`
        : `${property.name}\n${selectedSummary(property)}\n\nReply yes or book to continue.`,
      session: { sessionUuid, currentStage: "PROPERTY_SELECTED" },
    };
  }

  const dates = parseDatesFromText(text);
  const date = dates[0] || parseDateFromText(text);
  const guests = parseGuestsFromText(text);
  const bedrooms = parseBedroomsFromText(text);
  const budget = parseBudgetFromText(text);
  const amenities = parseAmenitiesFromText(text);
  const destination = parseDestinationFromText(text);

  if (/\b(agent|human|call|support|help|baat|phone)\b/.test(text)) {
    return {
      reply:
        hindi
          ? "Support request note kar li hai. Destination, dates aur guests bhej denge to Homevilas team ke liye details ready ho jayengi."
          : "Support request noted. Share destination, dates and guests, and I will keep the details ready for the Homevilas team.",
      session: { sessionUuid, currentStage: "HUMAN_HANDOFF" },
    };
  }

  if (destination) state.destination = destination;
  if (destination || dates.length || guests || bedrooms || budget.maxPrice) {
    state.selectedProperty = null;
  }
  if (guests) state.guests = guests;
  if (bedrooms) state.bedrooms = bedrooms;
  if (budget.maxPrice) state.maxPrice = budget.maxPrice;
  if (amenities.length) state.amenities = amenities;
  if (dates.length >= 2) {
    state.checkIn = dates[0];
    state.checkOut = dates[1];
  } else if (date) {
    if (/\b(check.?out|checkout|return|leaving)\b/.test(lower)) {
      state.checkOut = date;
    } else if (/\b(check.?in|checkin|arrival)\b/.test(lower)) {
      state.checkIn = date;
    } else if (!state.checkIn) {
      state.checkIn = date;
    } else if (!state.checkOut && date !== state.checkIn) {
      state.checkOut = date;
    } else {
      state.checkIn = date;
      state.checkOut = "";
    }
  }

  writeLocalState(state);

  let found = { rows: [], total: 0, url: "/listings" };
  if (state.destination) {
    try {
      found = await searchLocalProperties(state);
    } catch (_error) {
      found = { rows: [], total: 0, url: `/listings?city=${encodeURIComponent(state.destination)}` };
    }
  }

  const recommendations = found.rows || [];
  const toolResult = recommendations.length
    ? { result: { recommendations, count: found.total, listingsPath: found.url } }
    : undefined;

  if (!state.destination) {
    return {
      reply: hindi
        ? "Destination bataiye. Aap Hindi ya English me likh sakte hain, jaise Goa villa, Mumbai apartment, ya Udaipur pool villa."
        : "Tell me the destination. You can type naturally, for example Goa villa, Mumbai apartment, or Udaipur pool villa.",
      session: { sessionUuid, currentStage: "DESTINATION_REQUIRED" },
    };
  }
  if (!state.checkIn) {
    return {
      reply: recommendations.length
        ? hindi
          ? `${state.destination} me ${found.total} stays mile. Top options dikha raha hoon. Check-in date bataiye, jaise 21-09-2026.`
          : `I found ${found.total} stays in ${state.destination}. Showing top options. Please share check-in date, for example 21-09-2026.`
        : hindi
        ? `${state.destination} noted. Check-in date bataiye, jaise 21-09-2026.`
        : `${state.destination} noted. Please share check-in date, for example 21-09-2026.`,
      session: { sessionUuid, currentStage: "CHECKIN_REQUIRED" },
      toolResult,
    };
  }
  if (!state.checkOut) {
    return {
      reply: hindi
        ? `Check-in ${prettyDate(state.checkIn)} noted. Check-out date bataiye.`
        : `Check-in ${prettyDate(state.checkIn)} noted. Please share check-out date.`,
      session: { sessionUuid, currentStage: "CHECKOUT_REQUIRED" },
      toolResult,
    };
  }
  if (!state.guests) {
    return {
      reply: recommendations.length
        ? hindi
          ? `${state.destination}, ${prettyDate(state.checkIn)} to ${prettyDate(state.checkOut)} noted. Available options dikha raha hoon. Kitne guests honge?`
          : `${state.destination}, ${prettyDate(state.checkIn)} to ${prettyDate(state.checkOut)} noted. Showing available options. How many guests?`
        : hindi
        ? `${state.destination}, ${prettyDate(state.checkIn)} to ${prettyDate(state.checkOut)} noted. Kitne guests honge?`
        : `${state.destination}, ${prettyDate(state.checkIn)} to ${prettyDate(state.checkOut)} noted. How many guests?`,
      session: { sessionUuid, currentStage: "GUESTS_REQUIRED" },
      toolResult,
    };
  }
  return {
    reply: recommendations.length
      ? hindi
        ? `${state.destination} ke liye ${prettyDate(state.checkIn)} se ${prettyDate(state.checkOut)} tak ${state.guests} guests ke ${found.total} stays mile. Top options neeche hain. Sab dekhne ke liye: ${found.url}`
        : `I found ${found.total} stays in ${state.destination} for ${state.guests} guests from ${prettyDate(state.checkIn)} to ${prettyDate(state.checkOut)}. Top options are below. View all: ${found.url}`
      : hindi
      ? `${state.destination} ke liye details noted, lekin exact match nahi mila. Search page par filters adjust karke dekhiye: ${found.url}`
      : `I saved the details for ${state.destination}, but could not find an exact match. Try adjusting filters here: ${found.url}`,
    session: { sessionUuid, currentStage: recommendations.length ? "RESULTS_SHOWN" : "NO_RESULTS" },
    toolResult,
  };
};

export const createAiSession = async (initial = {}) => {
  try {
    const { data } = await api.post("/ai/sessions", {
      anonymousSessionId: getAnonymousSessionId(),
      initial,
    });
    const uuid = data?.session?.session_uuid || data?.session?.sessionUuid;
    if (uuid) storeAiSessionUuid(uuid);
    return data?.session;
  } catch (error) {
    if (isRecoverableAiFailure(error)) return createLocalSession(initial);
    throw error;
  }
};

export const getAiSession = async (sessionUuid) => {
  if (isLocalSession(sessionUuid)) return { session: createLocalSession(), messages: [] };
  try {
    const { data } = await api.get(`/ai/sessions/${encodeURIComponent(sessionUuid)}`);
    return data;
  } catch (error) {
    if (isRecoverableAiFailure(error)) return { session: createLocalSession(), messages: [] };
    throw error;
  }
};

export const getAiRuntimeHealth = async () => {
  const { data } = await api.get("/ai/internal/health");
  return data;
};

export const sendAiChatMessage = async (sessionUuid, content, options = {}) => {
  if (isLocalSession(sessionUuid)) {
    return localChatTurn(sessionUuid, content, options);
  }
  try {
    const { data } = await api.post(
      `/ai/sessions/${encodeURIComponent(sessionUuid)}/chat`,
      {
        content,
        messageType: options.messageType || "text",
        structuredData: options.structuredData || {},
      },
      {
        timeout: 120_000,
      }
    );
    return data;
  } catch (error) {
    if (isRecoverableAiFailure(error)) {
      const localSessionUuid = isLocalSession(sessionUuid) ? sessionUuid : createLocalSession().sessionUuid;
      return localChatTurn(localSessionUuid, content, options);
    }
    throw error;
  }
};

export const requestAiHandoff = async (sessionUuid, reason = "") => {
  if (isLocalSession(sessionUuid)) return { ok: true, reason };
  try {
    const { data } = await api.post(`/ai/sessions/${encodeURIComponent(sessionUuid)}/handoff`, { reason });
    return data;
  } catch (error) {
    if (isRecoverableAiFailure(error)) return { ok: true, reason };
    throw error;
  }
};

export const transcribeAiVoice = async (audioBlob, options = {}) => {
  const form = new FormData();
  form.append("audio", audioBlob, options.filename || "homevilas-voice.webm");
  form.append("language", options.language || "hi");
  if (options.prompt) form.append("prompt", options.prompt);
  const { data } = await api.post("/ai/voice/transcribe", form, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 60_000,
  });
  return data;
};

export const synthesizeAiVoice = async (text, options = {}) => {
  const { data } = await api.post(
    "/ai/voice/synthesize",
    {
      text,
      voice: options.voice,
      format: options.format || "mp3",
    },
    {
      responseType: "blob",
      timeout: 60_000,
    }
  );
  return data;
};
